import React, { useState, useEffect } from "react";

// Auth is cookie-based; these fetch calls were missing credentials:"include"
// entirely, so no auth cookie was ever sent cross-origin (frontend and
// backend are different origins in production) — every call here 401'd,
// including the paper/live broker mode switch. CSRF token: login/auth-me
// responses hand it back in the JSON body, stashed in localStorage under
// this key by App.js.
function csrfHeaders() {
  try {
    const stored = localStorage.getItem("roi_csrf_token");
    if (stored) return { "X-CSRF-Token": stored };
  } catch {}
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
}

function BrokerSettingsPanel() {
  const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/omega/v2`;
  const [brokerStatus, setBrokerStatus] = useState(null);
  const [settingMode, setSettingMode] = useState(false);

  const load = async () => {
    try {
      const s = await fetch(`${API_V2}/broker/status`, { credentials: "include", headers: csrfHeaders() }).then(r => r.json());
      setBrokerStatus(s);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const setMode = async (mode) => {
    setSettingMode(true);
    try {
      const s = await fetch(`${API_V2}/broker/mode`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ mode }),
      }).then(r => r.json());
      setBrokerStatus(s);
    } catch {} finally { setSettingMode(false); }
  };

  const BROKER_MODES = [
    { id: "PAPER", label: "Paper Only", desc: "All orders go to paper simulator — no real broker", color: "#4ade80" },
    { id: "LIVE_DISABLED", label: "Live Disabled", desc: "Real broker calls disabled; paper only", color: "#fbbf24" },
    { id: "LIVE_LIMITED", label: "Live Limited", desc: "Limited live access — only approved broker routes", color: "#60a5fa" },
    { id: "LIVE_FULL", label: "Live Full", desc: "Full real broker access — requires API credentials", color: "#f87171" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        fontSize: 10, lineHeight: 1.5, color: "var(--muted)", padding: "8px 12px",
        borderRadius: 6, border: "1px solid var(--border)", background: "var(--layer2)",
      }}>
        This is a separate, experimental broker-router subsystem — not the live trading
        pipeline. Your real trades already execute through the app's actual Public.com
        integration regardless of what's shown here.
      </div>
      <div className="card">
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", marginBottom: 12 }}>Broker Router (experimental)</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {BROKER_MODES.map(m => {
            const active = brokerStatus?.mode === m.id;
            return (
              <button key={m.id} disabled={settingMode} onClick={() => setMode(m.id)} style={{
                fontSize: 10, padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
                border: active ? `1px solid ${m.color}` : "1px solid var(--border)",
                background: active ? `${m.color}18` : "var(--layer2)",
                color: active ? m.color : "var(--muted)",
                opacity: settingMode ? 0.6 : 1,
              }}>{m.label}</button>
            );
          })}
        </div>
        {brokerStatus?.mode && (
          <div style={{ fontSize: 10, color: "var(--muted)" }}>
            {BROKER_MODES.find(m => m.id === brokerStatus.mode)?.desc}
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>Adapters</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(brokerStatus?.adapters || []).map(a => (
            <div key={a} style={{ padding: "4px 10px", borderRadius: 5, fontSize: 10, fontWeight: 600,
              border: a === "paper" ? "1px solid rgba(74,222,128,0.4)" : "1px solid var(--border)",
              background: a === "paper" ? "rgba(74,222,128,0.08)" : "var(--layer2)",
              color: a === "paper" ? "var(--green)" : "var(--muted)" }}>
              {a.toUpperCase()} {a === "paper" ? "✓ Active" : "· stub"}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 9, color: "var(--dim)", marginTop: 8 }}>
          Tradier, Coinbase, and Public adapters here are stubs for this experimental
          router only — pending its own separate credential wiring. Your real Public.com
          trading connection elsewhere in the app is unaffected and fully live.
        </div>
      </div>

      {brokerStatus?.recent_orders?.length > 0 && (
        <div className="card">
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", marginBottom: 10 }}>Recent Orders (last 20)</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
            <thead>
              <tr style={{ color: "var(--muted)", borderBottom: "1px solid var(--border)" }}>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>Broker</th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>Symbol</th>
                <th style={{ textAlign: "left", padding: "4px 6px" }}>Side</th>
                <th style={{ textAlign: "right", padding: "4px 6px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {[...brokerStatus.recent_orders].reverse().map((o, i) => (
                <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "4px 6px", color: "var(--muted)" }}>{o.broker}</td>
                  <td style={{ padding: "4px 6px", fontWeight: 600 }}>{o.order?.symbol || "—"}</td>
                  <td style={{ padding: "4px 6px", color: o.order?.side === "BUY" ? "var(--green)" : "var(--red)" }}>{o.order?.side || "—"}</td>
                  <td style={{ padding: "4px 6px", textAlign: "right", color: o.ok ? "var(--green)" : "var(--red)" }}>{o.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default BrokerSettingsPanel;
